import { Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import logoLight from "@/assets/logos/logo-light.svg"

/**
 * The way OUT of a report page, for EVERY viewer.
 *
 * /reports/:token is a public-token page with no site navigation of its own,
 * but it is also where authenticated people get parked: Rodauth's
 * login_redirect sends a guest with a generated report straight here, and
 * /app/plan/intake redirects a finished prospect here.
 *
 * Three audiences, three headers:
 *   - nobody signed in  → logo home + "Sign in". The token holder may be
 *     arriving from a bookmark, the emailed link, or an in-app webview where
 *     no session exists; without this the page had no exit at all
 *     (client report 2026-08-27).
 *   - guest             → who you are signed in as, stated plainly, plus real
 *     buttons for "Switch account" / "End session". The 2026-08-26 version put
 *     these in 12px grey text and people missed them on a phone.
 *   - coach/admin/member → "Back to your portal".
 *
 * Anonymous viewers still receive NO account data — `currentUser` is nil from
 * the controller for them, so nothing here can leak a session.
 *
 * SSR-safe (Report/** is in the SSR bundle): no browser-only imports, no
 * window/document access.
 */
export function ReportExitHeader({ currentUser }) {
  const isGuest = Boolean(currentUser?.isGuest)

  return (
    <div className="bg-white border-b border-zinc-200 px-4 sm:px-6 py-3 print:hidden">
      <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          {currentUser && !isGuest ? (
            <Link
              href={currentUser.portalPath}
              className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-kf-red"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to your portal
            </Link>
          ) : (
            <Link href="/" className="inline-flex items-center">
              <img src={logoLight} alt="Kingdom Factor" className="h-7 w-auto" />
            </Link>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {currentUser ? (
            <>
              {!isGuest && (
                <span className="hidden sm:inline text-xs text-zinc-500 truncate max-w-[16rem]">
                  {currentUser.name || currentUser.email}
                </span>
              )}
              <Link
                href="/login?switch=1"
                className="inline-flex items-center justify-center rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900"
              >
                Switch account
              </Link>
              <Link
                href="/logout"
                method="post"
                as="button"
                className="inline-flex items-center justify-center rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900"
              >
                {isGuest ? "End session" : "Sign out"}
              </Link>
            </>
          ) : (
            <Link
              href="/login"
              className="inline-flex items-center justify-center rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900"
            >
              Sign in
            </Link>
          )}
        </div>

        {isGuest && (
          <p className="w-full text-sm text-zinc-600">
            Signed in as{" "}
            <span className="font-semibold text-zinc-900 break-all">
              {currentUser.email}
            </span>
            . This is your assessment report. To reach a coach or member portal,
            switch to that account.
          </p>
        )}
      </div>
    </div>
  )
}

export default ReportExitHeader
